import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/shared/services/api.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { DatosDenuncia } from 'src/app/shared/models/datosDenuncia';
import { Usuarios } from 'src/app/dashboard/models/usuarios';
import { environment } from 'src/environments/environment.prod';
import { PlazosDenuncia } from 'src/app/model/plazosDenuncia';
import { sanitizeHtml } from 'src/app/shared/custom-validator/custom-validator.component';

/**
 * This Component Changed Phase Complaint
 */

@Component({
  selector: 'app-change-phase-complaint',
  templateUrl: './change-phase-complaint.component.html',
  styleUrls: ['./change-phase-complaint.component.css'],
})

export class ChangePhaseComplaintComponent implements OnInit {
  validatingForm: FormGroup; //validation forms
  typecomplaints: string; //model tye complaint
  idComplaintUrl: string; //url id
  name: string = ''; //id user
  phaseNext: string = ''; //
  show: boolean = false; //
  id_role: string = '0';
  usuarios = []; //
  usuariosReady = false; // flag to indicate usuarios list has finished loading
  phasePrevious: string; // phase previous
  contador2 = 0 //character count
  charCount: number = 0;
  maxCharCount: number = 4000;
  private lastValidHTML: string = '';
  state = 1; //disable button
  plazos: PlazosDenuncia[];
  isActiveIdPersonalizable: boolean = false; //Is Active Id Custom

  @Input() denuncia: DatosDenuncia; //data complaint
  assetsUrl: string = environment.baseAssetsUrl;
  constructor(
    private actRoute: ActivatedRoute,
    private apiService: ApiService,
    private auth: AuthService,
    private router: Router,
    private toastr: ToastrService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    // translation language
    let language = this.auth.getLanguage();
    if (language == '' || language == null) {
      language = 'es';
    }
    this.translate.setDefaultLang(language);

    // complaint id from the route
    this.idComplaintUrl = this.actRoute.snapshot.params.id;

    // validations form
    this.validatingForm = new FormGroup({
      user: new FormControl('', [Validators.required]),
      comentario: new FormControl('', [Validators.required, Validators.maxLength(4000)])
    });

    // check if idPersonalizado is active
    if (this.auth.getIdPersonalizable() == '1') {
      this.isActiveIdPersonalizable = true;
    }

    this.updateCharCount();
    this.handleUsers();
  }

  // Subscribe to value changes to always keep the character count updated.
  updateCharCount() {
    this.validatingForm.get('comentario')?.valueChanges.subscribe((value: string) => {
      this.charCount = value ? value.length : 0;
    });
  }

  // Validates HTML input, updates character count, and reverts to last valid value if limit is exceeded.
  onEditorChange(value: string) {
    const plainText = value
      ? value.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim()
      : '';

    if (plainText.length <= this.maxCharCount) {
      this.lastValidHTML = value;
      this.charCount = plainText.length;
    } else {
      this.validatingForm
        .get('comentario')
        ?.setValue(this.lastValidHTML, { emitEvent: false });

      const lastPlainText = this.lastValidHTML
        ? this.lastValidHTML.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim()
        : '';
      this.charCount = lastPlainText.length;
    }
  }

  //get value type complaint
  get user() {
    return this.validatingForm.get('user');
  }

  //get value type complaint
  get comentario() {
    return this.validatingForm.get('comentario');
  }

  /**
   * This method determines the target role/phrase and loads eligible users,
   * filtering out rejected users returned by compliance when present.
   */
  handleUsers(): void {
    const fase = this.denuncia[0].id_fase;

    // Determine role and phaseNext label from current phase
    const mapping = this.getRoleAndPhaseFor(fase);
    this.id_role = mapping.id_role;
    this.phaseNext = mapping.phaseNext;

    if (this.id_role === '0') return;

    // Load users for the determined role and filter rejects
    this.loadAndFilterUsers(this.id_role);
    this.show = true;
  }

  /**
   * Map current phase to target role and user-visible label.
   * Kept as a pure helper to make `selected()` easier to read and test.
   */
  private getRoleAndPhaseFor(fase: string): { id_role: string; phaseNext: string } {
    switch (fase) {
      case '1':
        return { id_role: '1', phaseNext: 'texto_373' };
      case '2':
        return { id_role: '1', phaseNext: 'Asignación Equipo' };
      case '3':
        return { id_role: '2', phaseNext: 'Investigación' };
      case '4':
        return { id_role: '1', phaseNext: 'Asignar desisor' };
      case '5':
        return { id_role: '3', phaseNext: 'Decisión' };
      case '6':
        return { id_role: '4', phaseNext: '' };
      default:
        return { id_role: '0', phaseNext: '' };
    }
  }

  /**
   * Loads users for a given role (via API) and applies rejection filters.
   * This includes optional exclusion of compliance-rejected users (CSV).
   */
  private loadAndFilterUsers(idRole: string): void {
    // mark not ready until the API resolves
    this.usuariosReady = false;
    this.apiService.searchUserByComplaintType(idRole, this.auth.getSociedad()).subscribe((usuario: Usuarios[]) => {
      if (!usuario || usuario.length === 0) {
        this.usuarios = [];
        this.usuariosReady = true;
        return;
      }

      const list = usuario || [];
      const rejectReceptor = this.denuncia && this.denuncia[0] ? this.denuncia[0].id_receptor_rechazado : null;

      // get compliance assigned user(s) to identify rejected users
      this.apiService.searchUserComplianceByIdSocietyRole(this.auth.getSociedad(), this.denuncia[0].id_denuncia).subscribe({
        next: (compResp: any) => {
          const compList = compResp || [];
          const rawCompReject = compList.length > 0 ? compList[0].id_usuario_comp_rechazado || null : null;
          const compRejectIds: string[] = rawCompReject ? rawCompReject.split(',').map((s: string) => s.trim()).filter(s => s.length > 0) : [];

          this.usuarios = list.filter((d: any) => {
            const uid = String(d.id_user ?? '');
            if (rejectReceptor && uid === String(rejectReceptor)) return false;
            if (compRejectIds.includes(uid)) return false;

            return true;
          });
          // mark that the list is ready after the filter is applied
          this.usuariosReady = true;
        },
        // fall back to receptor filter only
        error: () => {
          this.usuarios = rejectReceptor ? list.filter((d: any) => String(d.id_user ?? '') !== String(rejectReceptor)) : list;
          this.usuariosReady = true;
        }
      });
    });
  }

  checkUsersAvailability() {
    if (!this.usuariosReady) {
      // users are still loading; show info message and early return
      this.toastr.info(this.translate.instant('texto_203'));
      this.handleUsers();
      return;
    }

    if (this.usuarios == null || this.usuarios.length == 0) {
      this.toastr.error(this.translate.instant('texto_933'));
    }
  }

  changePhase() {
    // Prevent going back from admission/triaje
    if (this.denuncia[0].id_fase == '0' || this.denuncia[0].id_fase == '11') {
      this.router
        .navigateByUrl('/RefreshComponent', { skipLocationChange: true })
        .then(() => this.router.navigate(['editar-denuncia/', this.idComplaintUrl]));
      return;
    }

    const fase_retroceder = this.denuncia[0].id_fase - 1;

    let comentario = sanitizeHtml(this.comentario.value);
    if (!comentario) {
      comentario = 'Sin información';
    }

    //Service For Change Phase Complaint
    this.apiService
      .changeComplaintPhase(
        this.auth.getSociedad(),
        this.id_role,
        '',
        this.denuncia[0].id_denuncia,
        this.denuncia[0].id_fase,
        this.name,
        fase_retroceder.toString(),
        comentario
      )
      .subscribe((response) => {
        if (response == 200) {
          this.toastr.success(
            this.translate.instant('texto_697'),
            this.translate.instant('texto_698'),
          );

          // add user only when complait is in phase 3(asignar decisor to return to investigación)
          // or 5 (compliance to return decisión).
          if (this.denuncia[0].id_fase == '3' || this.denuncia[0].id_fase == '5') {
            // get deadlines.
            this.apiService.searchPlazosByIdDenuncia(this.denuncia[0].id_denuncia).subscribe((plazos: PlazosDenuncia[]) => {
              this.plazos = plazos;
              // add user to table QSC_DENUNCIAS_PLAZOS only if it don't exists yet.
              this.apiService.createDeadlinesByIdComplaint(this.auth.getSociedad(), this.denuncia[0].id_denuncia, this.plazos[0].plazo_investigacion, this.plazos[0].plazo_decisor, this.plazos[0].plazo_compliance, this.plazos[0].plazo_recepcion, this.name).subscribe(
                res => {
                  if (res == 200) {
                    this.toastr.success(this.translate.instant('texto_325'), this.translate.instant('texto_326'));
                  } else {
                    this.toastr.error(this.translate.instant('texto_298'), this.translate.instant('texto_297'));
                  }
                },
                err => {
                  this.toastr.error(this.translate.instant('texto_298'), this.translate.instant('texto_297'));
                }
              );

            });
          }

          //Send Mail
          let $clau = false;
          let clausulas_correo = localStorage.getItem("clausulas_correo");
          if (clausulas_correo == '1') {
            $clau = true;
          }
          let idDenunciaMostrado; // Variable to store the complaint ID to be displayed

          // Check if isActiveIdPersonalizable is true
          if (this.isActiveIdPersonalizable) {
            // If true, use identificador_denuncia if it exists, otherwise use id_denuncia
            idDenunciaMostrado = this.denuncia[0].identificador_denuncia
              ? this.denuncia[0].identificador_denuncia
              : this.denuncia[0].id_denuncia;
          } else {
            // If isActiveIdPersonalizable is false, only use id_denuncia
            idDenunciaMostrado = this.denuncia[0].id_denuncia;
          }
          let message = this.translate.instant('texto_700') + " " + idDenunciaMostrado + " " + this.translate.instant('texto_701');
          let url2 = environment.base + "/editar-denuncia/" + this.denuncia[0].id_denuncia;
          this.apiService.sendEmail(this.name, this.translate.instant('texto_702'), message, url2, $clau, this.translate.instant('texto_702'), 'gestor').subscribe(
            res => {
              if (res == 200) {
                this.toastr.success(this.translate.instant('texto_194'), this.translate.instant('texto_195'));
              } else {
                this.toastr.error(this.translate.instant('texto_193'), this.translate.instant('texto_195'));
              }
            });
          this.router
            .navigateByUrl('/RefreshComponent', { skipLocationChange: true })
            .then(() => {
              this.router.navigate(['dashboard/complaint-management/mis-denuncias/']);
            });
        } else {
          this.toastr.success(
            this.translate.instant('texto_699')
          );
        }
      },
        err => {
          this.toastr.error(this.translate.instant('texto_699') + ". " + err.error.error, this.translate.instant('texto_813'));
        });
  }

  onChangeUser(event) {
    //datos de la tabla QSC_CENTROS
    this.name = event.value;

    if (this.name) {
      this.state = 0;
    }
  }

  //character counter
  onKey(event) {
    var datos = document.getElementById("comentario");
    for (var i = 0; i < 1; i++) {
      var div = datos.getElementsByClassName('nw-editor__res')[i];
      this.contador2 = div.textContent.length
    }
  }
}
